import axios from "axios";

const api = axios.create({
  baseURL: "https://backend-mtaani.onrender.com/", // Update with your backend URL
});

// Attach token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/* PRODUCTS */
export const fetchProducts = () => api.get("/api/products/all");
export const createProduct = (data) => api.post("/api/products", data);
export const updateProduct = (id, data) => api.put(`/api/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/api/products/${id}`);

/* CATEGORIES */
export const fetchCategories = () => api.get("/api/categories");
export const createCategory = (data) => api.post("/api/categories", data);
export const updateCategory = (id, data) =>
  api.put(`/api/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/api/categories/${id}`);

// api/api.js
export const updateOrderAddItem = (orderId, payload) =>
  api.post(`/api/orders/${orderId}/items`, payload);

export const updateOrderItemQty = (orderId, itemId, payload) =>
  api.put(`/api/orders/${orderId}/items/${itemId}`, payload);

export const deleteOrderItem = (orderId, itemId) =>
  api.delete(`/api/orders/${orderId}/items/${itemId}`);

/* ORDERS */
export const fetchOrders = () => api.get("/api/orders");

// Full update — also used by admin for rider assignment
export const updateOrder = (orderId, data) =>
  api.put(`/api/orders/${orderId}`, data);

export const updateOrderItems = (orderId, items) => {
  return api.put(`/api/orders/${orderId}/items`, {
    items,
  });
};
// Update ONLY status (uses same endpoint but clean helper)
export const updateOrderStatus = (orderId, status) =>
  api.put(`/api/orders/${orderId}`, { status });

// Confirm order with customer and mark as shipped
export const confirmOrderAndShip = (orderId, note) =>
  api.put(`/api/orders/${orderId}`, {
    status: "shipped",
    ...(note?.trim() ? { confirmationNote: note.trim() } : {}),
  });

// Assign rider (correct endpoint)
export const assignRiderToOrder = (orderId, riderId) =>
  api.post(`/api/orders/${orderId}/assign-rider`, { riderId });

// Get one order
export const getOrderById = (orderId) => api.get(`/api/orders/${orderId}`);

/* PROFILE */
export const getProfile = () => api.get("/api/me");
export const updateProfile = (data) => api.put("/api/me", data);

/* RIDERS ADMIN */
export const fetchRiderOrdersAdmin = (riderId) =>
  api.get("/api/riders/orders", {
    params: riderId ? { riderId } : {},
  });

export const fetchRidersAdmin = () => api.get("/api/admin/riders/with-orders");

/* POS */
export const createPOSSale = (data) => api.post('/api/pos/sale', data);
export const fetchPOSSales = (params) => api.get('/api/pos/sales', { params });
export const getPOSReceipt = (id) => api.get(`/api/pos/receipt/${id}`);
export const voidPOSSale = (id, data) => api.put(`/api/pos/sale/${id}/void`, data);
export const refundPOSSale = (id, data) => api.post(`/api/pos/sale/${id}/refund`, data);
export const getPOSQueue = () => api.get('/api/pos/queue');
export const holdTransaction = (data) => api.post('/api/pos/hold', data);
export const getHeldTransactions = () => api.get('/api/pos/held');
export const recallHeld = (id) => api.delete(`/api/pos/held/${id}`);
export const updateHeld = (id, data) => api.put(`/api/pos/held/${id}`, data);

/* INVENTORY */
export const fetchInventory = (params) => api.get('/api/inventory', { params });
export const restockProduct = (productId, data) => api.post(`/api/inventory/restock/${productId}`, data);
export const adjustStock = (productId, data) => api.post(`/api/inventory/adjust/${productId}`, data);
export const fetchLowStock = () => api.get('/api/inventory/low-stock');
export const fetchInventoryHistory = (productId, params) => api.get(`/api/inventory/history/${productId}`, { params });
export const lookupBarcode = (code) => api.get(`/api/products/barcode/${code}`);

/* SHIFTS */
export const openShift = (data) => api.post('/api/shifts/open', data);
export const closeShift = (id, data) => api.put(`/api/shifts/${id}/close`, data);
export const getCurrentShift = () => api.get('/api/shifts/current');
export const addCashDrop = (id, data) => api.post(`/api/shifts/${id}/cash-drop`, data);
export const fetchShifts = (params) => api.get('/api/shifts', { params });

/* SUPPLIERS */
export const fetchSuppliers = (params) => api.get('/api/suppliers', { params });
export const createSupplier = (data) => api.post('/api/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/api/suppliers/${id}`, data);
export const deleteSupplier = (id) => api.delete(`/api/suppliers/${id}`);
export const fetchPurchaseOrders = (params) => api.get('/api/purchase-orders', { params });
export const createPurchaseOrder = (data) => api.post('/api/purchase-orders', data);
export const getPurchaseOrder = (id) => api.get(`/api/purchase-orders/${id}`);
export const receivePurchaseOrder = (id, data) => api.put(`/api/purchase-orders/${id}/receive`, data);
export const updatePOStatus = (id, data) => api.put(`/api/purchase-orders/${id}/status`, data);

/* DISCOUNTS */
export const fetchDiscounts = (params) => api.get('/api/discounts', { params });
export const createDiscount = (data) => api.post('/api/discounts', data);
export const updateDiscount = (id, data) => api.put(`/api/discounts/${id}`, data);
export const deleteDiscount = (id) => api.delete(`/api/discounts/${id}`);
export const validateDiscount = (data) => api.post('/api/discounts/validate', data);

/* REPORTS */
export const getDailySalesReport = (date) => api.get('/api/reports/sales/daily', { params: { date } });
export const getSalesRange = (params) => api.get('/api/reports/sales/range', { params });
export const getTopSellingProducts = (params) => api.get('/api/reports/products/top-selling', { params });
export const getInventoryMovement = (params) => api.get('/api/reports/inventory/movement', { params });
export const getCashierPerformance = (params) => api.get('/api/reports/cashier/performance', { params });
export const getRevenueSummary = (period) => api.get('/api/reports/revenue/summary', { params: { period } });

/* CUSTOMERS (LOYALTY) */
export const fetchCustomers = (params) => api.get('/api/customers', { params });
export const createCustomer = (data) => api.post('/api/customers', data);
export const searchCustomers = (q) => api.get('/api/customers/search', { params: { q } });
export const updateCustomer = (id, data) => api.put(`/api/customers/${id}`, data);
export const addLoyaltyPoints = (id, data) => api.post(`/api/customers/${id}/loyalty/add`, data);
export const redeemLoyaltyPoints = (id, data) => api.post(`/api/customers/${id}/loyalty/redeem`, data);
export const getCustomerHistory = (id, params) => api.get(`/api/customers/${id}/history`, { params });

/* APP CONFIG */
export const fetchPublicConfig = () => api.get('/api/app-config');
export const fetchAdminConfig = () => api.get('/api/admin/app-config');
export const updateConfig = (key, data) => api.put(`/api/admin/app-config/${key}`, data);
export const deleteConfig = (key) => api.delete(`/api/admin/app-config/${key}`);
export const seedConfig = () => api.post('/api/admin/app-config/seed');

/* CONTENT / HERO BANNERS */
export const fetchHeroBanners = () => api.get('/api/admin/content/hero');
export const createHeroBanner = (data) => api.post('/api/admin/content/hero', data);
export const updateHeroBanner = (id, data) => api.put(`/api/admin/content/hero/${id}`, data);
export const deleteHeroBanner = (id) => api.delete(`/api/admin/content/hero/${id}`);
export const reorderHeroBanners = (order) => api.put('/api/admin/content/hero/reorder', { order });
export const toggleHeroBanner = (id) => api.put(`/api/admin/content/hero/${id}/toggle`);

/* CONTENT / HERO PRODUCTS */
export const fetchHeroProducts = () => api.get('/api/admin/content/hero-products');
export const addHeroProduct = (productId) => api.post('/api/admin/content/hero-products', { productId });
export const removeHeroProduct = (id) => api.delete(`/api/admin/content/hero-products/${id}`);
export const reorderHeroProducts = (order) => api.put('/api/admin/content/hero-products/reorder', { order });

/* CUSTOMER SEGMENTS */
export const fetchSegments = () => api.get('/api/segments');
export const createSegment = (data) => api.post('/api/segments', data);
export const updateSegment = (id, data) => api.put(`/api/segments/${id}`, data);
export const deleteSegment = (id) => api.delete(`/api/segments/${id}`);
export const computeSegment = (id) => api.post(`/api/segments/${id}/compute`);
export const fetchSegmentMembers = (id, params) => api.get(`/api/segments/${id}/members`, { params });

/* CAMPAIGNS */
export const fetchCampaigns = (params) => api.get('/api/campaigns', { params });
export const createCampaign = (data) => api.post('/api/campaigns', data);
export const updateCampaign = (id, data) => api.put(`/api/campaigns/${id}`, data);
export const deleteCampaign = (id) => api.delete(`/api/campaigns/${id}`);
export const sendCampaign = (id) => api.post(`/api/campaigns/${id}/send`);
export const cancelCampaign = (id) => api.put(`/api/campaigns/${id}/cancel`);
export const getCampaign = (id) => api.get(`/api/campaigns/${id}`);

/* ANALYTICS */
export const fetchAnalyticsOverview = (period) => api.get('/api/analytics/overview', { params: { period } });
export const fetchRevenueTrend = (params) => api.get('/api/analytics/revenue-trend', { params });
export const fetchCustomerInsights = () => api.get('/api/analytics/customer-insights');
export const fetchProductPerformance = (params) => api.get('/api/analytics/product-performance', { params });
export const fetchOrderFunnel = (params) => api.get('/api/analytics/order-funnel', { params });
export const fetchGeographicData = () => api.get('/api/analytics/geographic');
export const fetchRealTimeStats = () => api.get('/api/analytics/real-time');

/* AUDIT LOG */
export const fetchAuditLog = (params) => api.get('/api/admin/audit-log', { params });
export const fetchAuditStats = () => api.get('/api/admin/audit-log/stats');

/* DEALS OF DAY */
export const fetchDealsOfDay = () => api.get('/api/admin/deals-of-day');
export const createDeal = (data) => api.post('/api/admin/deals-of-day', data);
export const updateDeal = (id, data) => api.put(`/api/admin/deals-of-day/${id}`, data);
export const deleteDeal = (id) => api.delete(`/api/admin/deals-of-day/${id}`);
export const reorderDeals = (order) => api.put('/api/admin/deals-of-day/reorder', { order });
export const notifyDeal = (id) => api.post(`/api/admin/deals-of-day/${id}/notify`);

/* PROMO MESSAGES */
export const fetchPromoMessages = () => api.get('/api/admin/content/promo');
export const createPromoMessage = (data) => api.post('/api/admin/content/promo', data);
export const updatePromoMessage = (id, data) => api.put(`/api/admin/content/promo/${id}`, data);
export const deletePromoMessage = (id) => api.delete(`/api/admin/content/promo/${id}`);
export const reorderPromoMessages = (ids) => api.put('/api/admin/content/promo/reorder', { ids });

/* ADMIN NOTIFICATIONS */
export const fetchAllNotifications = (params) => api.get('/api/admin/notifications', { params });
export const broadcastNotification = (data) => api.post('/api/admin/notifications/broadcast', data);
export const sendTargetedNotification = (data) => api.post('/api/admin/notifications/send', data);

export default api;
